import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Receipt, Loader2, Home, FileText } from "lucide-react";
import api from "@/lib/api";

const inr = (n) => new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(n || 0);
const gbp = (n) => new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP", maximumFractionDigits: 2 }).format(n || 0);
const cur = (n, c) => ((c || "inr").toLowerCase() === "gbp" ? gbp(n) : inr(n));
const fmtDate = (s) => {
  try { return new Date(s).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }); }
  catch { return s; }
};

export default function Orders() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/payments/orders")
      .then((r) => setOrders(r.data))
      .catch(() => toast.error("Failed to load your orders"))
      .finally(() => setLoading(false));
  }, []);

  const viewInvoice = async (invoiceId) => {
    try {
      const { data } = await api.get(`/invoices/${invoiceId}/pdf`);
      if (!data.pdf) { toast.error("Invoice not available"); return; }
      const blob = await (await fetch(data.pdf)).blob();
      window.open(URL.createObjectURL(blob), "_blank");
    } catch {
      toast.error("Could not open invoice");
    }
  };

  return (
    <div className="max-w-3xl" data-testid="orders-page">
      <h1 className="text-3xl font-semibold tracking-tight font-display text-slate-900 flex items-center gap-2">
        <Receipt className="h-7 w-7 text-emerald-600" /> Order History
      </h1>
      <p className="mt-2 text-sm text-slate-500">Your past paid orders and what each covered.</p>

      {loading ? (
        <div className="mt-16 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-emerald-600" /></div>
      ) : orders.length === 0 ? (
        <div className="mt-10 text-sm text-slate-400 border border-dashed border-slate-300 rounded-xl p-8 text-center" data-testid="orders-empty">
          You haven't paid for any products yet.
        </div>
      ) : (
        <div className="mt-6 space-y-4">
          {orders.map((o) => (
            <div key={o.session_id} data-testid="order-card" className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">Paid</span>
                  <span className="text-sm text-slate-500" data-testid="order-date">{fmtDate(o.paid_at || o.created_at)}</span>
                </div>
                <div className="flex items-center gap-3">
                  {o.invoice_id && (
                    <button onClick={() => viewInvoice(o.invoice_id)} data-testid="order-view-invoice" className="inline-flex items-center gap-1 text-xs text-emerald-700 hover:text-emerald-900 font-medium">
                      <FileText className="h-3.5 w-3.5" /> Invoice
                    </button>
                  )}
                  <span className="font-mono font-semibold text-slate-900" data-testid="order-total">{cur(o.amount, o.currency)}</span>
                </div>
              </div>
              <div className="mt-3 divide-y divide-slate-100">
                {o.items.map((it, idx) => (
                  <div key={idx} data-testid="order-line-item" className="flex items-start gap-3 py-2 text-sm">
                    <div className="flex-1 min-w-0">
                      <div className="text-slate-800">{it.name}</div>
                      <div className="text-xs text-slate-400 flex items-center gap-1 capitalize">
                        {it.service}
                        {it.property_address && <><span className="text-slate-300">·</span><Home className="h-3 w-3" /> <span className="truncate">{it.property_address}</span></>}
                      </div>
                    </div>
                    <span className="font-mono text-slate-700 shrink-0">{cur(it.amount, o.currency)}</span>
                  </div>
                ))}
              </div>
              <div className="mt-2 text-[11px] font-mono text-slate-400 truncate">Ref: {o.session_id}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
